import { UPLOADER_EVENTS, useUploady } from '@rpldy/uploady'
import React from 'react'
import { useMutation } from '@cedarjs/web'
import { toast } from 'react-hot-toast'

import {
  DELETE_INVALID_FILE,
  UPDATE_FILE_HASH,
  UPDATE_FILE_SIZE,
  INGEST_NEW_FILE_INTO_R2R_MUTATION,
} from 'src/hooks/requests/useUploadFile'
import { useUploadStatus } from 'src/components/UploadStatusIndicator/UploadStatusContext'
import Sentry from 'src/lib/sentry'

const useUploadEventListeners = () => {
  const { on, off } = useUploady()
  const [isUploading, setIsUploading] = React.useState(false)
  const [updateFileHash] = useMutation(UPDATE_FILE_HASH)
  const [updateFileSize] = useMutation(UPDATE_FILE_SIZE)
  const [deleteInvalidFile] = useMutation(DELETE_INVALID_FILE)
  const [ingestNewFileIntoR2r] = useMutation(INGEST_NEW_FILE_INTO_R2R_MUTATION)
  const { updateUpload } = useUploadStatus()

  React.useEffect(() => {
    //upload start listener
    const itemStartHandler = (item) => {
      setIsUploading(true)
    }

    on(UPLOADER_EVENTS.ITEM_START, itemStartHandler)

    //finished uploading listener
    const itemFinishHandler = async (item, options) => {
      setIsUploading(false)
      const searchParams = new URLSearchParams(window.location.search)
      const delay = parseInt(searchParams.get('delay') || '0') * 1000

      setTimeout(async () => {
        await updateFileSize({
          variables: {
            id: options.params.fileId,
            ext: item.file.name.split('.').pop(),
            localHash: options.params.localHash,
          },
          onError: async (error) => {
            // Update the upload status instead of showing a toast
            Sentry.captureException(error)
            updateUpload(options.params.uploadId, {
              status: 'error',
            })
            options.params.reject(error)
          },
          onCompleted: async () => {
            const maxFileSizeInMB = 120
            if (item.file.size > maxFileSizeInMB * 1024 * 1024) {
              const errorMessage =
                `File uploaded but AI Features disabled on this file due to file size (max ${maxFileSizeInMB}MB) at this moment.`
              updateUpload(options.params.uploadId, {
                status: 'error',
                error: errorMessage,
              })
              options.params.reject(new Error(errorMessage))
              return
            }
            updateUpload(options.params.uploadId, {
              status: 'ingesting',
            })
            await ingestNewFileIntoR2r({
              variables: {
                fileId: options.params.fileId,
              },
              onCompleted: () => {
                updateUpload(options.params.uploadId, { status: 'success' })
                options.params.resolve(options.params.fileId)
              },
              onError: (error) => {
                Sentry.captureException(error)
                updateUpload(options.params.uploadId, {
                  status: 'error',
                  error: `Failed to ingest file into DocHub`,
                })
                options.params.reject(error)
              },
            })
          },
        })
        // await updateFileHash({
        //   variables: {
        //     id: options.params.fileId,
        //     ext: item.file.name.split('.').pop(),
        //     localHash: options.params.localHash,
        //   },
        //   onError: async (error) => {
        //     toast.error(`${item.file.name} upload failed`, {
        //       duration: 3000,
        //       id: item.id,
        //     })
        //     options.params.reject(error)
        //   },
        //   onCompleted: () => {
        //     toast.success(`${item.file.name} uploaded successfully`, {
        //       duration: 3000,
        //       id: item.id,
        //     })
        //     options.params.resolve(item.file)
        //   },
        // })
      }, delay)
    }

    on(UPLOADER_EVENTS.ITEM_FINISH, itemFinishHandler)

    //upload error listener
    const itemErrorHandler = async (item, options) => {
      setIsUploading(false)

      const errorMessage = item?.uploadResponse?.data?.message

      updateUpload(options.params.uploadId, {
        status: 'error',
        error: errorMessage,
      })

      await deleteInvalidFile({
        variables: {
          id: options.params.fileId,
        },
      })
      options.params.reject(new Error(`Upload of ${item.file.name} failed`))
    }

    on(UPLOADER_EVENTS.ITEM_ERROR, itemErrorHandler)

    //unregister listeners
    return () => {
      off(UPLOADER_EVENTS.ITEM_START, itemStartHandler)
      off(UPLOADER_EVENTS.ITEM_FINISH, itemFinishHandler)
      off(UPLOADER_EVENTS.ITEM_ERROR, itemErrorHandler)
    }
  }, [])

  React.useEffect(() => {
    const handleUnload = (event) => {
      if (isUploading) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleUnload)
    return () => {
      window.removeEventListener('beforeunload', handleUnload)
    }
  }, [isUploading])
}

export default useUploadEventListeners
